import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DownOutlined, MoreOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Dropdown, Tooltip, Typography, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { LicenseActivationsTable } from "./licenseActivationsTable.js";
import { LicenseKeyDeleteConfirm } from "./licenseKeyDeleteConfirm.js";
import { LicenseKeyModal } from "./licenseKeyModal.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { LicenseKeyPill } from "../../../licenseKeyPill.js";
// Variants in Real Product Manager carry a trailing " PRO" to distinguish
// the paid build artefact from its free sibling. Inside the customer
// center the variant always sits under a subscription header that already
// names the product, so the suffix is redundant — strip for display only.
// TODO: remove once the " PRO" suffix is dropped at the source in Real
// Product Manager (the upstream variant naming should not bake the build
// flavour into the display name).
const stripProSuffix = (name)=>name.replace(/\s+PRO$/, "");
const LicenseKeyRow = observer(({ license, subscription, shouldShowProductColumn, shouldStackAllocation })=>{
    const { token } = theme.useToken();
    const { accountV2Store } = useStores();
    const [isActivationsExpanded, setIsActivationsExpanded] = useState(false);
    const [openModal, setOpenModal] = useState(undefined);
    const rowRef = useRef(null);
    const isFlashing = accountV2Store.flashLicenseId === license.data.id;
    // When the store signals this row as freshly created, scroll it into
    // view first, then the CSS pulse plays for the duration of the flash
    // window. `block: "center"` keeps the row clear of the sticky tab nav.
    useEffect(()=>{
        if (isFlashing && rowRef.current) {
            rowRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
        }
    }, [
        isFlashing
    ]);
    const { isReadOnly } = subscription;
    const envatoTooltip = !subscription.canBeModified ? __("Not available for codecanyon.net purchases.") : undefined;
    const items = [
        {
            key: "edit",
            disabled: !subscription.canBeModified || isReadOnly,
            label: envatoTooltip ? /*#__PURE__*/ _jsx(Tooltip, {
                title: envatoTooltip,
                children: __("Edit license key")
            }) : __("Edit license key"),
            onClick: ()=>setOpenModal("edit")
        },
        {
            key: "delete",
            danger: subscription.canBeModified && !isReadOnly,
            disabled: !subscription.canBeModified || isReadOnly,
            label: envatoTooltip ? /*#__PURE__*/ _jsx(Tooltip, {
                title: envatoTooltip,
                children: __("Delete license key")
            }) : __("Delete license key"),
            onClick: ()=>setOpenModal("delete")
        }
    ];
    const toggle = ()=>setIsActivationsExpanded((v)=>!v);
    return /*#__PURE__*/ _jsxs("div", {
        ref: rowRef,
        className: `rc-account-v2-subscription-card__license-row${isFlashing ? " rc-account-v2-subscription-card__license-row--flash" : ""}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-subscription-card__license-row-line",
                role: "button",
                tabIndex: 0,
                "aria-expanded": isActivationsExpanded,
                "aria-label": __("Show activations"),
                onClick: toggle,
                onKeyDown: (e)=>{
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle();
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(LicenseKeyPill, {
                        value: license.data.licenseKey
                    }),
                    shouldShowProductColumn && /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-subscription-card__license-product",
                        style: {
                            color: token.colorTextSecondary
                        },
                        children: stripProSuffix(license.data.productVariant.name)
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: `rc-account-v2-subscription-card__license-allocation${shouldStackAllocation ? " rc-account-v2-subscription-card__license-allocation--stacked" : ""}`,
                        "aria-label": __("Allocated: %s production · %s development", license.data.maxInstallationsProduction, license.data.maxInstallationsDevelopment),
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "rc-account-v2-subscription-card__license-seat-chip rc-account-v2-subscription-card__license-seat-chip--production",
                                title: __("Production websites"),
                                children: [
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: license.data.maxInstallationsProduction
                                    }),
                                    __("Production")
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "rc-account-v2-subscription-card__license-seat-chip rc-account-v2-subscription-card__license-seat-chip--development",
                                title: __("Development websites"),
                                children: [
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: license.data.maxInstallationsDevelopment
                                    }),
                                    __("Development")
                                ]
                            })
                        ]
                    }),
                    !!license.data.description && /*#__PURE__*/ _jsx(Typography.Paragraph, {
                        className: "rc-account-v2-subscription-card__license-description",
                        style: {
                            color: token.colorText,
                            marginBottom: 0
                        },
                        ellipsis: {
                            tooltip: license.data.description,
                            rows: 2
                        },
                        ...MATOMO_MASK,
                        children: license.data.description
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "rc-account-v2-subscription-card__license-actions",
                        role: "presentation",
                        onClick: (e)=>e.stopPropagation(),
                        children: /*#__PURE__*/ _jsx(Dropdown, {
                            menu: {
                                items
                            },
                            placement: "bottomRight",
                            trigger: [
                                "click"
                            ],
                            children: /*#__PURE__*/ _jsx(Button, {
                                type: "text",
                                size: "small",
                                icon: /*#__PURE__*/ _jsx(MoreOutlined, {}),
                                "aria-label": __("License actions")
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-subscription-card__license-chevron",
                        "aria-hidden": true,
                        children: isActivationsExpanded ? /*#__PURE__*/ _jsx(UpOutlined, {}) : /*#__PURE__*/ _jsx(DownOutlined, {})
                    })
                ]
            }),
            isActivationsExpanded && /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-subscription-card__activations-wrap",
                children: /*#__PURE__*/ _jsx(LicenseActivationsTable, {
                    license: license,
                    isReadOnly: isReadOnly
                })
            }),
            /*#__PURE__*/ _jsx(LicenseKeyModal, {
                subscription: subscription,
                license: license,
                open: openModal === "edit",
                onClose: ()=>setOpenModal(undefined)
            }),
            openModal === "delete" && /*#__PURE__*/ _jsx(LicenseKeyDeleteConfirm, {
                license: license,
                onClose: ()=>setOpenModal(undefined)
            })
        ]
    });
});
export { LicenseKeyRow };
